import React, { useState, useEffect } from 'react';
import { X, ArrowUpRight, ArrowDownRight, Scan, Sparkles, CheckSquare, Settings, Calculator, Bell } from 'lucide-react';
import { useAppStore, useNotificationStore } from '../store';
import { formatPrice, formatPercent } from '../utils/helpers';
import { useTranslation } from '../utils/translations';
import { NotificationCenter } from './NotificationCenter';

export default function MobileSidebar() {
  const { t } = useTranslation();
  const { sidebarOpen, setSidebarOpen, prices, selectedPair, setSelectedPair, activeTab, setActiveTab } = useAppStore();
  const { notifications, unreadCount } = useNotificationStore();
  const [showNotifications, setShowNotifications] = useState(false);

  // Log per debug mobile (solo in sviluppo)
  if (process.env.NODE_ENV === 'development') {
    useEffect(() => {
      console.log('📱 MobileSidebar - Notifications:', notifications.length);
      console.log('📱 MobileSidebar - Unread count:', unreadCount);
    }, [notifications, unreadCount]);
  }

  // Default pairs if prices not loaded
  const defaultPairs = [
    { symbol: 'EURUSD', bid: 1.0892, change: 0.23, trend: 'up' },
    { symbol: 'GBPUSD', bid: 1.2634, change: -0.15, trend: 'down' },
    { symbol: 'USDJPY', bid: 149.82, change: 0.45, trend: 'up' },
    { symbol: 'XAUUSD', bid: 2043.50, change: 1.12, trend: 'up' },
    { symbol: 'XAGUSD', bid: 24.35, change: -0.32, trend: 'down' },
    { symbol: 'US30', bid: 38245.00, change: 0.67, trend: 'up' },
    { symbol: 'US100', bid: 17892.50, change: 0.89, trend: 'up' },
  ];

  const displayPairs = prices.length > 0 ? prices : defaultPairs;

  const menuItems = [
    { id: 'scanner', label: t('scanner'), icon: Scan, isHome: true },
    { id: 'ai-analysis', label: 'AI Analisi', icon: Sparkles },
    { id: 'checklist', label: t('checklist'), icon: CheckSquare },
    { id: 'strategies', label: 'Strategies', icon: Settings },
    { id: 'calculator', label: 'Calculator', icon: Calculator },
  ];

  if (!sidebarOpen) return null;

  // Se le notifiche sono aperte, mostra solo il NotificationCenter
  if (showNotifications) {
    return (
      <div className="fixed inset-0 z-[100] lg:hidden">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={() => {
            setShowNotifications(false);
            setSidebarOpen(false);
          }}
        />

        {/* Notification Center Full Screen */}
        <div className="absolute inset-0 bg-slate-900 flex flex-col overflow-hidden" style={{ backgroundColor: '#0f172a' }}>
          {/* Header */}
          <div className="p-4 border-b border-slate-800 flex items-center justify-between bg-slate-900 flex-shrink-0" style={{ backgroundColor: '#0f172a' }}>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-500/20 rounded-lg border border-violet-500/30">
                <Bell className="w-5 h-5 text-violet-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Centro Notifiche</h3>
                {unreadCount > 0 && (
                  <p className="text-xs text-slate-400 mt-0.5">
                    {unreadCount} {unreadCount === 1 ? 'non letta' : 'non lette'}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={() => {
                setShowNotifications(false);
                setSidebarOpen(false);
              }}
              className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-400 hover:text-white" />
            </button>
          </div>
          
          {/* Content - NotificationCenter gestisce il suo layout interno */}
          <div className="flex-1 min-h-0 overflow-hidden">
            <NotificationCenter isMobile={true} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => {
          setSidebarOpen(false);
          setShowNotifications(false);
        }}
      />

      {/* Sidebar */}
      <div className="absolute left-0 top-0 h-full w-80 bg-slate-900 border-r border-slate-800/50 overflow-y-auto animate-slideIn">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-800/50 bg-gradient-to-r from-slate-900 to-slate-800">
          <h3 className="text-lg font-bold text-white">Menu</h3>
          <button
            onClick={() => {
              setSidebarOpen(false);
              setShowNotifications(false);
            }}
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Menu Pages */}
        <div className="p-4 space-y-2 border-b border-slate-800/50">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 px-2">
            Navigazione
          </div>
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => {
                  setActiveTab(item.id);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                  activeTab === item.id
                    ? 'bg-violet-500/20 border border-violet-500/50 text-violet-400'
                    : 'bg-slate-800/40 border border-slate-700/30 hover:bg-slate-800/60 text-slate-300'
                }`}
              >
                <Icon className={`w-5 h-5 ${activeTab === item.id ? 'text-violet-400' : 'text-slate-400'}`} />
                <span className="font-medium text-sm">{item.label}</span>
                {item.isHome && (
                  <span className="ml-auto text-xs px-2 py-0.5 bg-violet-500/20 text-violet-400 rounded">
                    Home
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Centro Notifiche Separato */}
        <div className="p-4 border-b border-slate-800/50">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 px-2">
            Notifiche
          </div>
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="w-full flex items-center gap-3 p-3 rounded-xl bg-slate-800/40 border border-slate-700/30 hover:bg-slate-800/60 text-slate-300 transition-all relative"
          >
            <Bell className="w-5 h-5 text-slate-400" />
            <span className="font-medium text-sm">Centro Notifiche</span>
            {unreadCount > 0 && (
              <span className="ml-auto bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
        </div>

        {/* Watchlist */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              {t('watch_list')}
            </h3>
            <span className="text-xs text-emerald-400 flex items-center gap-1">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              {t('live')}
            </span>
          </div>
          <div className="space-y-2">
            {displayPairs.slice(0, 10).map((pair) => (
              <button
                key={pair.symbol}
                onClick={() => {
                  setSelectedPair(pair.symbol);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${selectedPair === pair.symbol
                    ? 'bg-violet-500/20 border border-violet-500/50'
                    : 'bg-slate-800/40 border border-slate-700/30 hover:bg-slate-800/60'
                  }`}
              >
                <div className="flex items-center gap-2">
                  {pair.change >= 0 || pair.trend === 'up' ? (
                    <ArrowUpRight className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <ArrowDownRight className="w-4 h-4 text-red-400" />
                  )}
                  <span className="font-medium text-white text-sm">{pair.symbol}</span>
                </div>
                <div className="text-right">
                  <div className="text-xs text-white font-mono">
                    {formatPrice(pair.bid || pair.price, pair.symbol)}
                  </div>
                  <div
                    className={`text-xs ${(pair.change >= 0 || pair.trend === 'up') ? 'text-emerald-400' : 'text-red-400'
                      }`}
                  >
                    {formatPercent(pair.change)}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
